import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, Inject, SimpleChanges, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { ResponsiveViewService, responsiveViewToken, Sizes } from 'go-modules/responsive-view/responsive-view.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UADetect as UADetectClass, uaDetectToken } from 'go-modules/detect/ua-detect.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GoMaterialModule } from '@go-modules/go-material.module';

@Component({
	selector: 'ngx-player-controls-settings',
	templateUrl: './player-controls-settings.component.html',
	styleUrls: ['./player-controls-settings.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		GoMaterialModule,
		TranslateModule
	]
})
export class NgxPlayerControlsSettingsComponent implements OnInit, OnDestroy, OnChanges {
	@Input() public options: any;
	@Input() public player: any;
	@Input() public cuepointsActive: boolean;
	@Input() public volumeActive: boolean;
	@Input() public liveMode: boolean;
	@Input() public settingsActive: boolean;
	@Input() public captionsOn: boolean;
	@Output() public toggleCaptions: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() public toggleAudioDescription: EventEmitter<void> = new EventEmitter<void>();
	@Output() public toggleCommentCaptions: EventEmitter<void> = new EventEmitter<void>();

	public captionsEnabled: boolean = false;
	public commentCaptionsEnabled: boolean = false;
	public audioDescriptionEnabled: boolean = false;
	public sourceVolumeLevel: number = 100;
	public volumeLevel: number = 100;
	public isXSmallScreen: boolean = false;
	private destroy$ = new Subject<void>();

	constructor (
		private dialog: MatDialog,
		private translate: TranslateService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(responsiveViewToken) private responsiveView: ResponsiveViewService,
		@Inject(uaDetectToken) public UADetect: UADetectClass
	) {}

	public ngOnInit (): void {
		this.isXSmallScreen = this.responsiveView.isAtMost(Sizes.XSMALL);
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.cuepointsActive) {
			this.commentCaptionsEnabled = changes.cuepointsActive.currentValue;
		}
		if (changes.captionsOn) {
			this.captionsEnabled = changes.captionsOn.currentValue;
		}
	}

	public shouldShowClosedCaptionsToggle (): boolean {
		return !this.options.isClosedCaptionsDisabled;
	}

	public toggleCaps (): void {
		if (this.player.youtubePlayer) {
			const modalData = {
				title: this.translate.instant('modal-cc-not-available-title'),
				message: this.translate.instant('modal-cc-not-available-message'),
				confirmText: this.translate.instant('common_close'),
				confirmStyle: 'primary-btn'
			};

			const dialogRef = this.dialog.open(MessageDialogComponent, {
				data: modalData
			});

			dialogRef.afterClosed()
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					this.toggleCaptions.emit(false);
				});
		} else {
			this.toggleCaptions.emit(!this.player.captionsEnabled());
		}
	}

	public shouldShowSourceSyncAndAudio (): boolean {
		const player = this.options.playerSyncSourcePlayer;
		return !!player && !player.isDocumentPlayer;
	}

	public getSourceMute (): boolean {
		const player = this.options.playerSyncSourcePlayer;
		if (player && player.getMute != null) {
			return player.getMute();
		}
		return this.sourceVolumeLevel !== 0;
	};

	public toggleSourceMute (): void {
		const player = this.options.playerSyncSourcePlayer;
		if (player && player.getMute != null) {
			if (player.getMute()) {
				player.setVolume(this.sourceVolumeLevel);
				player.setMute(false);
				if (this.UADetect.isIOS() && !this.getMute()) {
					this.toggleMute();
				}
			} else {
				player.setMute(true);
			}
		}
	};

	public getMute (): boolean {
		const player = this.options.playerSyncSessionPlayer || this.player;
		if (player.getMute != null) {
			return player.getMute();
		}
		return this.volumeLevel !== 0;
	};

	public toggleMute (): void {
		const player = this.options.playerSyncSessionPlayer || this.player;
		if (player.getMute()) {
			player.setVolume(this.volumeLevel);
			player.setMute(false);
			this.saveMuteLocalStorage(false);
			if (this.UADetect.isIOS() && !this.getSourceMute()) {
				this.toggleSourceMute();
			}
		} else {
			player.setMute(true);
			this.saveMuteLocalStorage(true);
		}
	};

	public saveMuteLocalStorage (isMuted: boolean): void {
		localStorage.setItem(this.userService.currentUser.user_id + '-mute-player', String(isMuted));
	};

	public updateSourceVolumeLevel (value: number): void {
		const player = this.options.playerSyncSourcePlayer;
		player?.setVolume?.(value);
	}

	public updateVolumeLevel (value: number): void {
		const player = this.options.playerSyncSessionPlayer || this.player;
		player?.setVolume?.(value);
	}

	public toggleLiveMode (): void {
		if (!this.player.isPlaying()) {
			this.player.play();
		} else {
			this.player.stop();
		}
	};
}
